import React, {useState} from 'react';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';
import {getSortedTranslations} from 'assets/i18n';

import {Button} from 'client/common/components/lego';
import genSrcSet from 'client/common/utils/srcset';
import cfg from 'client/common/utils/client-config';
import {createUrl} from 'client/common/utils/history';
import {getCurrentOrg} from 'client/common/hooks/orgs';
import {useSurvey} from 'client/common/hooks/survey';

import {CreativeSurveyData, useCreative} from 'client/creatives/hooks/creatives';
import {CreativesBoxType} from 'client/creatives/types';
import CreativeCard from 'client/creatives/components/creative-card';

import WelcomeImage from './images/welcome.png';
import WelcomeImage2x from './images/welcome@2x.png';
import WelcomeImage3x from './images/welcome@3x.png';
import StarImage from './images/star.svg';
import BackIcon from './images/back.svg';
import LinkIcon from './images/link.svg';

import './welcome-modal.css';

const i18n = I18N.creatives.onboarding.welcomeModal;
const b = b_.with('welcome-modal');

export interface WelcomeModalProps {
    className?: string;
    onClose: () => void;
    boxName: CreativesBoxType;
}

enum Step {
    Welcome,
    Chips,
    Demo,
    Final
}

export default function WelcomeModal(props: WelcomeModalProps) {
    const {className = '', onClose, boxName} = props;

    const [step, setStep] = useState(Step.Welcome);

    // eslint-disable-next-line
    const demoSlug = cfg.demoSlugs[boxName][0];
    const demoSurvey = useSurvey(demoSlug);
    const demoCreative = useCreative({
        slug: demoSlug,
        ...((demoSurvey?.data as Partial<CreativeSurveyData>) ?? {})
    });
    const org = getCurrentOrg();

    function handleClickContinue() {
        if (step < Step.Final) {
            setStep(step + 1);
        } else {
            onClose();
        }
    }

    function handleClickBack() {
        setStep(step - 1 || Step.Welcome);
    }

    return (
        <div className={`${b()} ${className}`}>
            <h2 className={b('title')}>{i18n[boxName].step[step].title}</h2>
            <p className={b('description')}>{i18n[boxName].step[step].description}</p>
            {step === Step.Welcome && (
                <img
                    className={b('image')}
                    src={WelcomeImage}
                    srcSet={genSrcSet([WelcomeImage, WelcomeImage2x, WelcomeImage3x])}
                />
            )}
            {step === Step.Chips && (
                <>
                    <div className={b('chips')}>
                        {getSortedTranslations(i18n[boxName].step[step].chips).map(text => {
                            return (
                                <span className={b('chip')} key={text}>
                                    {text}
                                </span>
                            );
                        })}
                    </div>
                    <span className={b('chips-hint')}>{i18n[boxName].step[step].hint}</span>
                </>
            )}
            {step === Step.Demo && demoSurvey.status === 'success' && (
                <div className={b('demo-survey')}>
                    <CreativeCard
                        slug={demoSlug}
                        state={demoSurvey?.data?.state}
                        name={demoSurvey?.data?.name}
                        stat={demoCreative.stat}
                        creative={demoCreative.creative}
                    />
                    <span className={b('demo-hint')}>{i18n.itIsExample}</span>
                </div>
            )}
            {step === Step.Final && (
                <a
                    className={b('service-link')}
                    href={createUrl({
                        link: boxName === 'bordomatik' ? '/creatives' : '/bordomatiks',
                        extraSearchParams: {org}
                    })}
                >
                    <img src={LinkIcon} className={b('link-icon')} />
                    {i18n[boxName].step[3].testLabel}
                    <img className={b('star', {side: 'left'})} src={StarImage} />
                    <img className={b('star', {side: 'right'})} src={StarImage} />
                </a>
            )}
            <div className={b('buttons')}>
                <div className={b('button-back')} onClick={handleClickBack} hidden={step === 0}>
                    <img src={BackIcon} />
                    <span>{i18n.button.back}</span>
                </div>
                {step === Step.Demo && (
                    <Button
                        size='m'
                        view='default'
                        type='link'
                        url={createUrl({link: `${boxName}/statistics/${demoSlug}`})}
                        className={b('button', {type: 'showDemo'})}
                    >
                        {i18n.button.showDemo}
                    </Button>
                )}
                <Button
                    size='m'
                    view='action'
                    className={b('button', {type: 'next'})}
                    onClick={handleClickContinue}
                >
                    {step === Step.Demo ? i18n.button.testVideo : i18n.button.continue}
                </Button>
                <span className={b('pagination')}>{`${step + 1} ${i18n.from} ${
                    Step.Final + 1
                }`}</span>
            </div>
        </div>
    );
}
